import React, { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../../../context/AuthContext";
import { API_URL } from "./constants";
import { executeMutation, executeQuery } from "../../../graphql/client";

import UserAvatar from "../../common/UserAvatar/UserAvatar";
import { uploadProfilePhoto } from "../../../api/profilePhotos";
import "./TeamTab.css";

// #134 phase 6: team roster + captain writes via graphql (were REST
// ?type=teamMembers / promoteCaptain / demoteCaptain).
const TEAM_MEMBERS_QUERY = `
  query TeamMembers {
    teamMembers {
      admins { username email sub name enabled status joinedAt userId profileImgKey }
      captains { username email sub name enabled status joinedAt userId profileImgKey }
    }
  }
`;

const SET_CAPTAIN_ROLE_MUTATION = `
  mutation SetCaptainRole($emailOrUsername: String!, $promote: Boolean!) {
    setCaptainRole(emailOrUsername: $emailOrUsername, promote: $promote) {
      success
      username
    }
  }
`;

// Phase H admin override mutation — surfaces alongside the existing
// Promote / Demote actions so a super-admin can upload (or clear) any
// team member's profile photo without going through the user's own
// account flow. The graphql mutation owner-checks pass because the
// caller is admin.
const SET_PROFILE_IMAGE = `
  mutation SetMemberProfileImage($userId: ID!, $imgKey: String) {
    setUserProfileImage(userId: $userId, imgKey: $imgKey) {
      userId
      profileImgKey
    }
  }
`;

// Admin-only Team management view. Shows current Admins (read-only — manage
// those in the AWS Console for safety) and Captains (promote / remove).
// Backend gates on isAdmin, so even if a Captain somehow loaded this tab
// nothing here would work.
const TeamTab = () => {
  const { getAuthHeaders } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [captains, setCaptains] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [newCaptainEmail, setNewCaptainEmail] = useState("");
  const [busyAction, setBusyAction] = useState("");
  const [actionMessage, setActionMessage] = useState("");
  // Phase H. Hidden file input shared across rows — we set
  // pendingPhotoMember to the row being edited, then click the input;
  // the change handler reads the file and uploads on that member's
  // behalf.
  const fileInputRef = useRef(null);
  const [pendingPhotoMember, setPendingPhotoMember] = useState(null);

  const loadTeam = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await executeQuery(TEAM_MEMBERS_QUERY);
      setAdmins(data?.teamMembers?.admins || []);
      setCaptains(data?.teamMembers?.captains || []);
    } catch (err) {
      setError(err.message || "Failed to load team members");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTeam();
  }, [loadTeam]);

  const handlePromote = async (e) => {
    e.preventDefault();
    const email = newCaptainEmail.trim().toLowerCase();
    if (!email) return;
    setBusyAction(`promote:${email}`);
    setActionMessage("");
    try {
      await executeMutation(SET_CAPTAIN_ROLE_MUTATION, {
        emailOrUsername: email,
        promote: true,
      });
      setActionMessage(`Promoted ${email} to Captain.`);
      setNewCaptainEmail("");
      await loadTeam();
    } catch (err) {
      setActionMessage(`Promote failed: ${err.message || err}`);
    } finally {
      setBusyAction("");
    }
  };

  const handleDemote = async (member) => {
    const label = member.email || member.username;
    if (!window.confirm(`Remove ${label} from Captains?`)) return;
    setBusyAction(`demote:${member.username}`);
    setActionMessage("");
    try {
      await executeMutation(SET_CAPTAIN_ROLE_MUTATION, {
        emailOrUsername: member.username,
        promote: false,
      });
      setActionMessage(`Removed ${label} from Captains.`);
      await loadTeam();
    } catch (err) {
      setActionMessage(`Remove failed: ${err.message || err}`);
    } finally {
      setBusyAction("");
    }
  };

  const updateMemberPhoto = (target, nextKey) => {
    const map = (list) =>
      list.map((m) =>
        m.username === target.username
          ? { ...m, profileImgKey: nextKey || null }
          : m
      );
    setAdmins(map);
    setCaptains(map);
  };

  const handlePhotoUploadClick = (member) => {
    if (!member.userId) {
      setActionMessage(
        `Can't upload photo for ${member.email}: no socialUsers userId on record.`
      );
      return;
    }
    setPendingPhotoMember(member);
    // Slight defer so React commits the state update before the click —
    // otherwise the change handler runs against a stale closure.
    setTimeout(() => {
      if (fileInputRef.current) fileInputRef.current.click();
    }, 0);
  };

  const handlePhotoFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    const member = pendingPhotoMember;
    setPendingPhotoMember(null);
    if (!file || !member) return;
    setBusyAction(`photo:${member.username}`);
    setActionMessage("");
    try {
      const { s3Key } = await uploadProfilePhoto({
        file,
        targetUserId: member.userId,
      });
      await executeMutation(SET_PROFILE_IMAGE, {
        userId: member.userId,
        imgKey: s3Key,
      });
      updateMemberPhoto(member, s3Key);
      setActionMessage(`Uploaded photo for ${member.email}.`);
    } catch (err) {
      setActionMessage(`Photo upload failed: ${err.message || err}`);
    } finally {
      setBusyAction("");
    }
  };

  const handlePhotoClear = async (member) => {
    if (!member.userId) {
      setActionMessage(
        `Can't clear photo for ${member.email}: no socialUsers userId on record.`
      );
      return;
    }
    if (!window.confirm(`Remove ${member.email}'s profile photo?`)) return;
    setBusyAction(`photo:${member.username}`);
    setActionMessage("");
    try {
      await executeMutation(SET_PROFILE_IMAGE, {
        userId: member.userId,
        imgKey: null,
      });
      updateMemberPhoto(member, null);
      setActionMessage(`Cleared photo for ${member.email}.`);
    } catch (err) {
      setActionMessage(`Photo clear failed: ${err.message || err}`);
    } finally {
      setBusyAction("");
    }
  };

  const renderMemberRow = (member, canRemove) => {
    const photoBusy = busyAction === `photo:${member.username}`;
    return (
      <tr key={member.username}>
        <td className="team-tab__avatar-cell">
          <UserAvatar
            name={member.name || member.email}
            email={member.email}
            imgKey={member.profileImgKey}
            size={36}
          />
        </td>
        <td>{member.email || member.username}</td>
        <td>{member.name || ""}</td>
        <td className="team-tab__status">{member.status || ""}</td>
        <td className="team-tab__actions">
          <button
            type="button"
            className="team-tab__btn"
            onClick={() => handlePhotoUploadClick(member)}
            disabled={photoBusy}
            title="Upload a profile photo for this member"
          >
            {photoBusy
              ? "Uploading…"
              : member.profileImgKey
                ? "Change photo"
                : "Upload photo"}
          </button>
          {member.profileImgKey && (
            <button
              type="button"
              className="team-tab__btn"
              onClick={() => handlePhotoClear(member)}
              disabled={photoBusy}
            >
              Clear photo
            </button>
          )}
          {canRemove && (
            <button
              type="button"
              className="team-tab__btn team-tab__btn--danger"
              onClick={() => handleDemote(member)}
              disabled={busyAction === `demote:${member.username}`}
            >
              {busyAction === `demote:${member.username}` ? "Removing…" : "Remove"}
            </button>
          )}
        </td>
      </tr>
    );
  };

  return (
    <div className="team-tab">
      <h2 className="team-tab__title">Team</h2>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={handlePhotoFile}
        style={{ display: "none" }}
      />

      {error && <div className="team-tab__error">Error: {error}</div>}
      {actionMessage && (
        <div className="team-tab__info">{actionMessage}</div>
      )}

      <h3 className="team-tab__section-title">Admins ({admins.length})</h3>
      <p className="team-tab__hint">
        Admin membership is managed in the AWS Cognito console — change it
        carefully (you can lock yourself out).
      </p>
      {loading ? (
        <div className="team-tab__info">Loading…</div>
      ) : (
        <table className="team-tab__table">
          <thead>
            <tr>
              <th aria-label="Photo"></th>
              <th>Email</th>
              <th>Name</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {admins.length === 0 ? (
              <tr>
                <td colSpan={5} className="team-tab__empty">
                  No one in the Admins group.
                </td>
              </tr>
            ) : (
              admins.map((m) => renderMemberRow(m, false))
            )}
          </tbody>
        </table>
      )}

      <h3 className="team-tab__section-title">Captains ({captains.length})</h3>
      <p className="team-tab__hint">
        Captains can manage events, photos, locations, and post to GroupMe.
        They cannot approve members or see the user database.
      </p>
      {!loading && (
        <table className="team-tab__table">
          <thead>
            <tr>
              <th aria-label="Photo"></th>
              <th>Email</th>
              <th>Name</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {captains.length === 0 ? (
              <tr>
                <td colSpan={5} className="team-tab__empty">
                  No captains yet. Add one below.
                </td>
              </tr>
            ) : (
              captains.map((m) => renderMemberRow(m, true))
            )}
          </tbody>
        </table>
      )}

      <form className="team-tab__add" onSubmit={handlePromote}>
        <label htmlFor="new-captain-email">Promote a user to Captain</label>
        <div className="team-tab__add-row">
          <input
            id="new-captain-email"
            type="email"
            value={newCaptainEmail}
            onChange={(e) => setNewCaptainEmail(e.target.value)}
            placeholder="user@example.com"
            autoComplete="off"
            required
          />
          <button
            type="submit"
            className="team-tab__btn team-tab__btn--primary"
            disabled={
              !newCaptainEmail.trim() ||
              busyAction === `promote:${newCaptainEmail.trim().toLowerCase()}`
            }
          >
            {busyAction.startsWith("promote:") ? "Adding…" : "Add Captain"}
          </button>
        </div>
        <p className="team-tab__hint">
          The user must already have a Cognito account (they sign up via the
          normal Login page first).
        </p>
      </form>
    </div>
  );
};

export default TeamTab;
